import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../contexts/ProjectContext';
import { useAuth } from '../contexts/AuthContext';
import { FolderIcon, PencilIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import { projectsAPI } from '../api/api';
import { updateProject as updateProjectAPI } from '../api/projectApi';
import EditProjectModal from '../components/modals/EditProjectModal';

const ProjectsPage = () => {
  const navigate = useNavigate();
  const { projects, selectedProject, selectProject, setProjects, updateProject } = useProject();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  // Debug: Component render'da state'leri log'la
  // console.log('🔍 ProjectsPage render:', {
  //   user: user,
  //   isAuthenticated: isAuthenticated,
  //   projectsLength: projects?.length,
  //   loading: loading
  // });

  // Sayfa yüklendiğinde projects data'sını yükle
  useEffect(() => {
    // console.log('🔍 ProjectsPage useEffect triggered:', {
    //   projectsLength: projects?.length,
    //   userAuthenticated: user?.isAuthenticated,
    //   user: user
    // });

    const loadProjects = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // console.log('📊 Loading projects data...');
        const response = await projectsAPI.getProjects();
        
        if (response.success) {
          // console.log('✅ Projects loaded successfully:', response.data);
          setProjects(response.data);
        } else {
          // console.error('❌ Failed to load projects:', response.message);
          setError(response.message);
        }
      } catch (error) {
        // console.error('💥 Error loading projects:', error);
        setError('Projeler yüklenirken bir hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    // Her zaman authenticated kullanıcı için yükle (projects state'ine bakmaksızın)
    if (isAuthenticated) {
      // console.log('🚀 User authenticated, loading projects...');
      loadProjects();
    } else {
      // console.log('⏳ User not authenticated, setting loading to false');
      setLoading(false);
    }
  }, [isAuthenticated, setProjects]); // user?.isAuthenticated yerine isAuthenticated kullan

  const getProjectIcon = (project) => {
    // Eğer proje objesi icon alanına sahipse onu kullan
    if (project && project.project_icon) {
      // Gerçek icon dosyası varsa onu göster
      if (project.project_icon.startsWith('icons8-')) {
        return (
          <img 
            src={`/project-icons/${project.project_icon}.png`}
            alt={project.project_icon}
            className="w-8 h-8 object-contain"
          />
        );
      }
    }
    
    // Varsayılan icon
    return '📁';
  };

  const handleEditProject = (project, e) => {
    e.stopPropagation(); // Proje seçimini engellemek için
    setEditingProject(project);
    setEditError('');
  };

  const handleSaveProject = async (formData) => {
    setEditLoading(true);
    setEditError('');

    try {
      const projectData = {
        project_name: formData.projectName,
        project_code: formData.projectCode,
        project_icon: formData.projectIcon,
        background_image: formData.backgroundImage,
      };

      const response = await updateProjectAPI(editingProject.id, projectData);
      
      if (response.success) {
        const updatedProjectData = {
          id: response.id,
          project_name: response.project_name,
          project_code: response.project_code,
          project_icon: response.project_icon,
          background_image: response.background_image,
          is_active: response.is_active,
          hotel_id: response.hotel_id,
          owner_department_id: response.owner_department_id
        };
        
        updateProject(updatedProjectData);
        setEditingProject(null);
      } else {
        setEditError(response.message || 'Proje güncellenirken bir hata oluştu.');
      }
    } catch (error) {
      setEditError(error.message || 'Proje güncellenirken bir hata oluştu.');
    } finally {
      setEditLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingProject(null);
    setEditError('');
  };

  const getProjectTypeColor = (type) => {
    switch (type) {
      case 'Software':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      case 'Mobile':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'Web':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300';
      case 'Support':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300';
      case 'AI':
        return 'bg-pink-100 text-pink-800 dark:bg-pink-900/20 dark:text-pink-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="p-8 bg-gray-50 dark:bg-gray-900 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Projeler yükleniyor...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-8 bg-gray-50 dark:bg-gray-900 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Hata Oluştu</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto">
                 <div className="mb-8 flex justify-between items-center">
           <div>
             <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Projects</h1>
             <p className="text-gray-600 dark:text-gray-400">Proje seçimi yapın ve board'da çalışmaya başlayın.</p>
           </div>
           <button
                           onClick={() => {
                // console.log('🔄 Manual refresh button clicked');
                if (isAuthenticated) {
                  setLoading(true);
                  projectsAPI.getProjects().then(response => {
                    if (response.success) {
                      // console.log('✅ Manual refresh successful:', response.data);
                      setProjects(response.data);
                    } else {
                      // console.error('❌ Manual refresh failed:', response.message);
                      setError(response.message);
                    }
                    setLoading(false);
                  }).catch(error => {
                    // console.error('💥 Manual refresh error:', error);
                    setError('Manuel yenileme sırasında hata oluştu');
                    setLoading(false);
                  });
                } else {
                  // console.log('❌ User not authenticated for manual refresh');
                }
              }}
             className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
           >
             <span>🔄</span>
             <span>Yenile</span>
           </button>
         </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects && Array.isArray(projects) && projects.filter(project => project && project.id).map((project) => (
            <div
              key={project.id}
              onClick={() => selectProject(project)}
              className="cursor-pointer rounded-lg border p-6 transition-all duration-200 hover:shadow-lg hover:border-blue-300 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
            >
              {/* Project icon and basic info */}
              <div className="flex items-center mb-4">
                <div className="mr-3 flex items-center justify-center w-8 h-8">
                  {getProjectIcon(project)}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {project.project_name || project.name}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {project.project_code || project.key}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/projects/${project.id}/statuses`);
                    }}
                    className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    title="Status Yönetimi"
                  >
                    <Cog6ToothIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => handleEditProject(project, e)}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    title="Projeyi Düzenle"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Project type badge */}
              <div className="mb-3">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getProjectTypeColor(project.type)}`}>
                  {project.type || 'General'}
                </span>
              </div>

              {/* Project details from database */}
              <div className="space-y-2 mb-4">
                {project.owner_department_id && (
                  <div className="text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Departman ID: </span>
                    <span className="text-gray-900 dark:text-white">{project.owner_department_id}</span>
                  </div>
                )}
                {project.created_at && (
                  <div className="text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Oluşturulma: </span>
                    <span className="text-gray-900 dark:text-white">
                      {new Date(project.created_at).toLocaleDateString('tr-TR')}
                    </span>
                  </div>
                )}
                <div className="text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Durum: </span>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    project.is_active ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
                  }`}>
                    {project.is_active ? 'Aktif' : 'Pasif'}
                  </span>
                </div>
              </div>

              {/* Project description */}
              {project.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                  {project.description}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Empty state */}
        {(!projects || !Array.isArray(projects) || projects.length === 0) && (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <FolderIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Henüz hiç proje oluşturulmamış.</p>
            </div>
          </div>
        )}
      </div>

      {/* Edit Project Modal */}
      {editingProject && (
        <EditProjectModal
          project={editingProject}
          onSave={handleSaveProject}
          onCancel={handleCancelEdit}
          isLoading={editLoading}
          error={editError}
        />
      )}
    </div>
  );
};

export default ProjectsPage;